/**
 * Variant Selector - Moon Moon Agency
 * Custom element réutilisable hors page produit (ex: MM-Keypoints).
 * Gère la sélection de variante, la mise à jour du prix CTA et la sync Skio.
 * Ne recharge pas la section (pas de fetchVariantData).
 */

class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;

		this.container = this.closest('form');
		if (!this.container) {
			window.log('warn', '🔄 [VARIANT_SELECTOR]', 'No form container found');
			return;
		}

		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		this.addEventListener('change', this.onVariantChange);
		this.getSelectedOptions();
		this.getSelectedVariant();
	}

	onVariantChange() {
		setTimeout(() => {
			this.getSelectedOptions();
			this.getSelectedVariant();

			if (this.currentVariant) {
				this._updateFormVariantId();
				this._updateCtaPrice();
				this._syncSkio();
			} else {
				window.log('warn', '🔄 [VARIANT_SELECTOR]', 'No matching variant found', { options: this.options });
			}
		}, 10);
	}

	_updateFormVariantId() {
		const input = this.container.querySelector('[name="id"]');
		if (input) input.value = this.currentVariant.id;
	}

	_updateCtaPrice() {
		const priceEl = this.container.querySelector('[data-skio-cta-price]');
		if (!priceEl) return;

		const moneyFormat = window.Shopify?.moneyFormat || '{{amount_with_comma_separator}}€';
		const formatted = this._formatMoney(this.currentVariant.price, moneyFormat);
		priceEl.textContent = formatted;

		const compareEl = this.container.querySelector('[data-skio-compare-at]');
		const originalEl = this.container.querySelector('[data-skio-original-price]');

		if (compareEl && originalEl) {
			const hasDiscount = this.currentVariant.compare_at_price > this.currentVariant.price;
			compareEl.classList.toggle('mm-none', !hasDiscount);
			originalEl.classList.toggle('mm-none', !hasDiscount);

			if (hasDiscount) {
				compareEl.textContent = this._formatMoney(this.currentVariant.compare_at_price, moneyFormat);
				originalEl.textContent = this._formatMoney(this.currentVariant.compare_at_price, moneyFormat);
			}
		}
	}

	_syncSkio() {
		const skioPickerEl = this.container.querySelector('skio-plan-picker');
		if (!skioPickerEl) return;

		const hasSellingPlans = this.currentVariant.selling_plan_allocations?.length > 0;
		skioPickerEl.setAttribute('selectedVariant', JSON.stringify(this.currentVariant));

		if (!hasSellingPlans) {
			skioPickerEl.updateComplete?.then(() => {
				skioPickerEl.selectSellingPlanGroup?.(null);
			});
		}
	}

	_formatMoney(cents, format) {
		if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
		const amount = (cents / 100).toFixed(2).replace('.', ',');
		return format
			.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
			.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100))
			.replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
			.replace(/\{\{\s*amount\s*\}\}/, amount);
	}

	getSelectedOptions() {
		const checkboxes = this.querySelectorAll('[data-variant-input]:checked');
		this.options = Array.from(checkboxes, cb => cb.value).filter(Boolean);
	}

	getSelectedVariant() {
		const variants = this.getVariantJSON();
		if (!variants.length) { this.currentVariant = null; return; }

		this.currentVariant = variants.find(variant =>
			variant.options?.every((option, i) => this.options[i] === option)
		) || null;
	}

	getVariantJSON() {
		if (!this.variantData) {
			const jsonEl = this.querySelector('[type="application/json"]');
			if (!jsonEl) { this.variantData = []; return this.variantData; }
			try {
				this.variantData = JSON.parse(jsonEl.textContent.trim()) || [];
			} catch {
				this.variantData = [];
			}
		}
		return this.variantData;
	}
}

if (!customElements.get('variant-selector')) {
	customElements.define('variant-selector', VariantSelector);
}
